import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layers, Plus, Pause, Play, Users, Settings, ArrowUpRight, ShieldCheck, Ban } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useTenant } from "../lib/tenant";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";

const MODULES = [
    { key: "nucleo", label: "Núcleo", desc: "Fideicomisos, KYC, Patrimonio, Tesorería" },
    { key: "v1", label: "V1 Garantía", desc: "Tape, elegibilidad, borrowing base, covenants" },
    { key: "a1", label: "A1 IA documental", desc: "Extracción con LLM" },
    { key: "a2", label: "A2 Conexión a fuente", desc: "API/webhook/SFTP" },
    { key: "a3", label: "A3 Motor de cálculo", desc: "Intereses, fees, waterfalls" },
    { key: "a4", label: "A4 Alta frecuencia", desc: "Conciliación diaria" },
    { key: "a5", label: "A5 Revolventes", desc: "Extensión V1" },
    { key: "a6", label: "A6 Data room + analytics", desc: "Analytics avanzados" },
];

export default function AdminTenants() {
    const { list, refresh, loading } = useTenant();
    const [openCreate, setOpenCreate] = useState(false);
    const [openOnboarding, setOpenOnboarding] = useState(null);  // tenant object
    const [suspendModal, setSuspendModal] = useState(null);      // tenant object
    const [motivo, setMotivo] = useState("");

    // Create form state
    const [form, setForm] = useState({
        slug: "", nombre_legal: "", nombre_comercial: "",
        contacto_email: "", contacto_telefono: "", contacto_direccion: "",
        modulos: ["nucleo"],
    });

    const toggleModulo = (m) => {
        setForm(f => ({
            ...f,
            modulos: f.modulos.includes(m)
                ? f.modulos.filter(x => x !== m)
                : [...f.modulos, m],
        }));
    };

    const submitCreate = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                slug: form.slug,
                nombre_legal: form.nombre_legal,
                nombre_comercial: form.nombre_comercial || form.nombre_legal,
                contacto: {
                    email: form.contacto_email || null,
                    telefono: form.contacto_telefono || null,
                    direccion: form.contacto_direccion || null,
                },
                modulos_contratados: form.modulos,
            };
            const { data } = await api.post("/admin/tenants", payload);
            toast.success(`Tenant creado: ${data.slug}`);
            setOpenCreate(false);
            setForm({
                slug: "", nombre_legal: "", nombre_comercial: "",
                contacto_email: "", contacto_telefono: "", contacto_direccion: "",
                modulos: ["nucleo"],
            });
            refresh();
            setOpenOnboarding(data);  // abrir wizard onboarding
        } catch (err) {
            toast.error("Error al crear tenant", { description: err?.response?.data?.detail });
        }
    };

    const onSuspend = async () => {
        if (!suspendModal || !motivo) return;
        try {
            const formData = new FormData();
            formData.append("motivo", motivo);
            await api.post(`/admin/tenants/${suspendModal.id}/suspender`, formData);
            toast.success(`Tenant ${suspendModal.slug} suspendido`);
            setSuspendModal(null);
            setMotivo("");
            refresh();
        } catch (err) {
            toast.error("Error", { description: err?.response?.data?.detail });
        }
    };

    const onReactivate = async (tenant) => {
        try {
            await api.post(`/admin/tenants/${tenant.id}/reactivar`);
            toast.success(`Tenant ${tenant.slug} reactivado`);
            refresh();
        } catch (err) {
            toast.error("Error", { description: err?.response?.data?.detail });
        }
    };

    return (
        <div className="p-8 lg:p-10 space-y-6 max-w-[1400px]" data-testid="admin-tenants-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Multi-tenant · A7
                    </div>
                    <h1 className="font-display text-4xl tracking-tight">
                        Tenants operados por Trafalgar
                    </h1>
                    <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
                        Cada tenant opera con aislamiento absoluto. Puedes crear, suspender o
                        reactivar clientes, y usar el selector del header para operar como
                        cualquiera de ellos con trazabilidad completa en la bitácora.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Link to="/admin/dashboard-operacion">
                        <Button variant="outline" data-testid="btn-dashboard-operacion">
                            <ArrowUpRight className="h-4 w-4 mr-1" />
                            Dashboard operación
                        </Button>
                    </Link>
                    <Button onClick={() => setOpenCreate(true)} data-testid="btn-new-tenant">
                        <Plus className="h-4 w-4 mr-1" />
                        Nuevo tenant
                    </Button>
                </div>
            </div>

            <div className="rounded-md border border-border bg-card overflow-hidden">
                <table className="trf-table">
                    <thead><tr>
                        <th>Slug</th>
                        <th>Nombre legal</th>
                        <th>Estado</th>
                        <th>Módulos</th>
                        <th>SLA disp.</th>
                        <th>Fecha alta</th>
                        <th></th>
                    </tr></thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan="7" className="text-center text-muted-foreground py-6">Cargando…</td></tr>
                        )}
                        {!loading && list.map((t) => (
                            <tr key={t.id} data-testid={`tenant-row-${t.slug}`}>
                                <td className="font-medium flex items-center gap-2">
                                    <Layers className="h-3.5 w-3.5 text-[color:var(--brand-dark-gold)]" />
                                    {t.slug}
                                </td>
                                <td className="text-sm">{t.nombre_legal}</td>
                                <td>
                                    <span className={
                                        t.estado === "activo" ? "badge-exito"
                                            : t.estado === "suspendido" ? "badge-error"
                                            : "badge-gold"
                                    }>
                                        {t.estado}
                                    </span>
                                </td>
                                <td>
                                    <div className="flex flex-wrap gap-1">
                                        {(t.modulos_contratados || []).map((m) => (
                                            <span key={m} className="badge-gold text-[9px] uppercase">{m}</span>
                                        ))}
                                    </div>
                                </td>
                                <td className="tabular text-xs">{t.sla?.disponibilidad_pct || "-"}%</td>
                                <td className="tabular text-[10px] text-muted-foreground">
                                    {t.fecha_alta?.substring(0, 10)}
                                </td>
                                <td>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="ghost" size="sm"
                                            onClick={() => setOpenOnboarding(t)}
                                            data-testid={`btn-onboard-${t.slug}`}
                                            title="Crear admin user + clonar catálogo"
                                            className="h-7 text-[11px]"
                                        >
                                            <Users className="h-3 w-3 mr-1" />
                                            Onboarding
                                        </Button>
                                        {t.estado === "activo" && t.slug !== "trafalgar" && (
                                            <Button
                                                variant="ghost" size="sm"
                                                onClick={() => setSuspendModal(t)}
                                                data-testid={`btn-suspend-${t.slug}`}
                                                className="h-7 text-[11px] text-[color:var(--brand-coral)]"
                                            >
                                                <Pause className="h-3 w-3 mr-1" />
                                                Suspender
                                            </Button>
                                        )}
                                        {t.estado === "suspendido" && (
                                            <Button
                                                variant="ghost" size="sm"
                                                onClick={() => onReactivate(t)}
                                                data-testid={`btn-reactivate-${t.slug}`}
                                                className="h-7 text-[11px] text-[color:var(--brand-exito)]"
                                            >
                                                <Play className="h-3 w-3 mr-1" />
                                                Reactivar
                                            </Button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {!loading && list.length === 0 && (
                            <tr><td colSpan="7" className="text-center text-muted-foreground py-6">
                                Sin tenants aún. Crea el primero.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal: Nuevo tenant */}
            <Dialog open={openCreate} onOpenChange={setOpenCreate}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tenant-create-modal">
                    <DialogHeader>
                        <DialogTitle className="font-display">Nuevo tenant</DialogTitle>
                        <DialogDescription>
                            Alta administrativa. Después usa "Onboarding" para crear su admin user
                            y clonar catálogo contable.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submitCreate} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="slug">Slug (URL-safe)*</Label>
                                <Input id="slug" required value={form.slug}
                                    onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
                                    placeholder="acme-financiera"
                                    data-testid="input-tenant-slug" />
                            </div>
                            <div>
                                <Label htmlFor="nombre_legal">Nombre legal*</Label>
                                <Input id="nombre_legal" required value={form.nombre_legal}
                                    onChange={e => setForm(f => ({ ...f, nombre_legal: e.target.value }))}
                                    placeholder="Acme Financiera SA de CV"
                                    data-testid="input-tenant-nombre-legal" />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="nombre_comercial">Nombre comercial</Label>
                            <Input id="nombre_comercial" value={form.nombre_comercial}
                                onChange={e => setForm(f => ({ ...f, nombre_comercial: e.target.value }))}
                                placeholder="Acme"
                                data-testid="input-tenant-nombre-comercial" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="c_email">Email contacto</Label>
                                <Input id="c_email" type="email" value={form.contacto_email}
                                    onChange={e => setForm(f => ({ ...f, contacto_email: e.target.value }))}
                                    data-testid="input-tenant-email" />
                            </div>
                            <div>
                                <Label htmlFor="c_tel">Teléfono</Label>
                                <Input id="c_tel" value={form.contacto_telefono}
                                    onChange={e => setForm(f => ({ ...f, contacto_telefono: e.target.value }))}
                                    data-testid="input-tenant-telefono" />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="c_dir">Dirección</Label>
                            <Input id="c_dir" value={form.contacto_direccion}
                                onChange={e => setForm(f => ({ ...f, contacto_direccion: e.target.value }))}
                                data-testid="input-tenant-direccion" />
                        </div>
                        <div>
                            <Label>Módulos contratados</Label>
                            <div className="mt-2 grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                                {MODULES.map(m => (
                                    <label key={m.key}
                                        className={`flex items-start gap-2 p-2 rounded-sm border cursor-pointer transition-colors ${
                                            form.modulos.includes(m.key)
                                                ? "border-[color:var(--brand-dark-gold)] bg-[color:var(--brand-gold)]/10"
                                                : "border-border hover:bg-muted"
                                        }`}
                                        data-testid={`modulo-${m.key}`}
                                    >
                                        <Checkbox
                                            checked={form.modulos.includes(m.key)}
                                            onCheckedChange={() => toggleModulo(m.key)}
                                        />
                                        <div className="text-xs">
                                            <div className="font-medium">{m.label}</div>
                                            <div className="text-muted-foreground text-[10px]">{m.desc}</div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={() => setOpenCreate(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" data-testid="btn-submit-create-tenant"
                                disabled={!form.slug || !form.nombre_legal || form.modulos.length === 0}>
                                <ShieldCheck className="h-4 w-4 mr-1" />
                                Crear tenant
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Modal: Onboarding tenant */}
            <OnboardingModal
                tenant={openOnboarding}
                onClose={() => setOpenOnboarding(null)}
                onDone={() => { setOpenOnboarding(null); refresh(); }}
            />

            {/* Modal: Suspender */}
            <Dialog open={!!suspendModal} onOpenChange={(o) => { if (!o) { setSuspendModal(null); setMotivo(""); } }}>
                <DialogContent className="max-w-md" data-testid="tenant-suspend-modal">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2">
                            <Ban className="h-5 w-5 text-[color:var(--brand-coral)]" />
                            Suspender tenant
                        </DialogTitle>
                        <DialogDescription>
                            <b>{suspendModal?.slug}</b> — Los usuarios de este tenant NO podrán operar
                            hasta que reactives. Registrado en bitácora.
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <Label htmlFor="motivo-susp">Motivo*</Label>
                        <Textarea id="motivo-susp" value={motivo} onChange={e => setMotivo(e.target.value)}
                            placeholder="Impago de facturas mes anterior…"
                            data-testid="input-suspend-motivo" rows={3} />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => { setSuspendModal(null); setMotivo(""); }}>
                            Cancelar
                        </Button>
                        <Button variant="destructive" onClick={onSuspend} disabled={!motivo}
                            data-testid="btn-confirm-suspend">
                            <Pause className="h-4 w-4 mr-1" />
                            Suspender
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


function OnboardingModal({ tenant, onClose, onDone }) {
    const [form, setForm] = useState({ email: "", nombre: "", password: "" });
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (tenant) {
            setForm({ email: "", nombre: "", password: "" });
            setResult(null);
        }
    }, [tenant]);

    const submit = async (e) => {
        e.preventDefault();
        if (!tenant) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/tenants/${tenant.id}/onboarding`, form);
            setResult(data);
            toast.success("Onboarding completo", {
                description: `Admin ${data.admin_email} creado + catálogo clonado (${data.catalogo_clonado} cuentas)`,
            });
        } catch (err) {
            toast.error("Error onboarding", { description: err?.response?.data?.detail });
        } finally { setBusy(false); }
    };

    if (!tenant) return null;

    return (
        <Dialog open={!!tenant} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="tenant-onboarding-modal">
                <DialogHeader>
                    <DialogTitle className="font-display">
                        Onboarding · {tenant.slug}
                    </DialogTitle>
                    <DialogDescription>
                        Crea el usuario administrador del tenant y clona catálogo contable
                        + tarifas base.
                    </DialogDescription>
                </DialogHeader>
                {!result ? (
                    <form onSubmit={submit} className="space-y-3">
                        <div>
                            <Label htmlFor="ob-email">Email admin*</Label>
                            <Input id="ob-email" type="email" required value={form.email}
                                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                                placeholder={`admin@${tenant.slug}.com`}
                                data-testid="input-onboard-email" />
                        </div>
                        <div>
                            <Label htmlFor="ob-nombre">Nombre*</Label>
                            <Input id="ob-nombre" required value={form.nombre}
                                onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                                data-testid="input-onboard-nombre" />
                        </div>
                        <div>
                            <Label htmlFor="ob-pw">Contraseña inicial* (mín. 8)</Label>
                            <Input id="ob-pw" required type="text" value={form.password}
                                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                                placeholder="Al menos 8 caracteres"
                                data-testid="input-onboard-password" />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={onClose}>
                                Cerrar
                            </Button>
                            <Button type="submit" disabled={busy || form.password.length < 8}
                                data-testid="btn-submit-onboard">
                                {busy ? "Creando…" : "Ejecutar onboarding"}
                            </Button>
                        </DialogFooter>
                    </form>
                ) : (
                    <div className="space-y-3 text-sm">
                        <div className="rounded-sm border border-[color:var(--brand-exito)]/30 bg-[color:var(--brand-exito)]/5 p-3">
                            <div className="font-semibold text-[color:var(--brand-exito)]">
                                Onboarding completado
                            </div>
                            <div className="text-xs mt-2 space-y-1">
                                <div>Admin: <code>{result.admin_email}</code></div>
                                <div>TOTP secret: <code className="text-[10px]">{result.admin_mfa_secret}</code></div>
                                <div>Cuentas contables clonadas: {result.catalogo_clonado}</div>
                                <div>Tarifas base clonadas: {result.tarifas_clonadas}</div>
                            </div>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                            Comparte estas credenciales por canal seguro. El usuario podrá cambiar
                            su contraseña al primer login.
                        </p>
                        <DialogFooter>
                            <Button onClick={onDone} data-testid="btn-close-onboard">Cerrar</Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
